/**
 * 계약 정보 수정 컴포넌트
 * 계약의 기본 정보를 수정하는 컴포넌트입니다.
 * 계약명, 계약일시, 계약자명, 이용기간, 보증금, 이용료 등의 정보를 수정할 수 있습니다.
 */

import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { BillingDetail } from "src/api/billingSchedule/billing-type";
import {
  ContractDetail,
  ContractorNoti,
  ContractStep,
  DetailTotalData,
  disabledBeforeContractAccept,
  disabledBeforeContractPlan,
  disabledEsignEdit,
  priceChangeStatus2,
} from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { providerDetailAsync } from "src/api/provider/provider-api";
import { ProviderPayletterModel, ProviderPopbillModel } from "src/api/provider/provider-types";
import { Select } from "src/api/public-types";
import { BaseButton, BaseCheckbox, BaseDatePicker, BaseInput, BaseRadio, BaseSelect, BaseTooltip } from "src/components";
import BaseSection from "src/components/layout/BaseSection";
import { changePgcodeLabel, getPgcodeLabel } from "src/pages/product/product-types";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";
import { numberToStringWithComma, parsedPhoneNumber, ViewYmdFormat, YmdFormat } from "src/utils";
import useContactApi from "../../hooks/useContractApi";
import useContractRenewal from "../../hooks/useContractRenewal";
import useEsignApi from "../../hooks/useEsignApi";
import DirectFileUpload from "../DirectFileUpload";
import ModifyAmountModalAccordion from "../modal/ModifyAmountModalAccordion";
import SnapShotModal from "../modal/SnapShotModal";
import ModuSignContractSection from "../moduSign/ModuSignContractSection";
import EsignOnLink from "./EsignOnLink";
// eslint-disable-next-line no-useless-escape
const checkEmail = /([\w-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;

/**
 * 계약 정보 수정 기본 props 인터페이스
 */
interface BasicProps {
  detailData: DetailTotalData; // 상세 데이터
  saveFlags: boolean; // 저장 플래그
  popupState: boolean; // 팝업 상태
  setEditable: any; // 편집 가능 상태 설정 함수
  setSaveFlags: any; // 저장 플래그 설정 함수
  setData: Function; // 데이터 업데이트 함수
  setPopupState: any; // 팝업 상태 설정 함수
  setCommonModals: any; // 공통 모달 설정 함수
  setEditConfrim: any; // 편집 확인 설정 함수
  contractSignType: { directContract: boolean; isEsign: boolean; isModuSign: boolean };
  onChangeContractSignType: (contractSignType: { directContract: boolean; isEsign: boolean; isModuSign: boolean }) => void;
}

/**
 * 결제 상태 옵션
 */
const payStatusOption = [
  { label: "결제대기", value: "PAY_WAIT" },
  { label: "결제완료", value: "PAY_COMPLETE" },
];

/** 이용기간(계약일시 포함)을 더 이상 수정할 수 없는 계약 상태. 계약예정까지만 수정할 수 있다 */
const USE_PERIOD_LOCKED_STEPS: Array<string> = [
  ContractStep.APPLY_CANCEL,
  ContractStep.CONTRACT_ACCEPT,
  ContractStep.USE_APPROVAL,
  ContractStep.USE_PROGRESS,
  ContractStep.TERMINATE_RECEIVED,
  ContractStep.TERMINATE_COMPLETE,
  ContractStep.USE_COMPLETE,
];

/**
 * 이용 시작일 / 종료일로 계약 기간(개월)을 계산한다.
 * 두 날짜가 모두 있을 때만 갱신하고, 아니면 기존 값을 유지한다.
 */
const getContractTerm = (startTime?: string, endTime?: string, fallback?: number) => {
  if (!startTime || !endTime) return fallback;
  return parseInt(String(moment.duration(moment(endTime).diff(moment(startTime))).asMonths()));
};

/**
 * 계약 정보 수정 컴포넌트
 * @param detailData 상세 데이터
 * @param saveFlags 저장 플래그
 * @param setEditable 편집 가능 상태 설정 함수
 * @param setData 데이터 업데이트 함수
 * @param setSaveFlags 저장 플래그 설정 함수
 * @param setCommonModals 공통 모달 설정 함수
 * @param setPopupState 팝업 상태 설정 함수
 * @param setEditConfrim 편집 확인 설정 함수
 */
const ContractInfoEdit = ({
  detailData,
  saveFlags,
  setEditable,
  setData,
  setSaveFlags,
  setCommonModals,
  setPopupState,
  setEditConfrim,
  contractSignType,
  onChangeContractSignType,
}: BasicProps) => {
  const { openToast } = useToast();
  const { editContractData } = useContactApi();
  const { getPartnerEsignCheck } = useEsignApi();
  const { contract, contractManage, product, scheduleList } = detailData;

  const { setBaseModal } = useModal();

  // 상태 관리
  const [textGrey] = useState("contents-container__grid ");
  const [editContract, setEditContract] = useState<ContractDetail>({}); // 수정 중인 계약 정보
  const [contractNameError, setContractNameError] = useState(""); // 계약명 에러 텍스트
  const [isSnapShotOpen, setIsSnapShotOpen] = useState(false); // 스냅샷 모달 상태

  const [isModifyAmountOpen, setIsModifyAmountOpen] = useState(false); // 금액 수정 모달 상태
  const [, setSelectedPeriodOptions] = useState<Select[]>([]); // 선택된 기간 옵션
  const [editRentalSchedule, setEditRentalSchedule] = useState<BillingDetail>(); // 수정 중인 임대 스케줄
  const [editNonTaxNotiList, setEditNonTaxNotiList] = useState<Array<ContractorNoti>>([]); // 비과세 알림 목록
  const [editTaxNotiList, setEditTaxNotiList] = useState<Array<ContractorNoti>>([]); // 과세 알림 목록
  const [s2TaxNotiOne, setS2TaxNotiOne] = useState<any>({
    // 세금계산서 알림 상태
    email: "",
    isDeleted: false,
    contact: "",
    receiveNotiType: "TAX_INVOICE",
    description: "",
    errorTextEmail: "",
  });
  /** 현재 계약이 재계약인지 (재계약은 계약시작일을 수정할 수 없다) */
  const { isRenewal } = useContractRenewal(detailData);

  /** 계약예정 이후 단계에서는 이용기간을 수정할 수 없다 */
  const isUsePeriodLocked = USE_PERIOD_LOCKED_STEPS.includes(String(contract?.contractStep));

  //프로바이더 정산정보 유효성 체크를 위한 프로바이더 상세 정보
  const [providerNormalCardApi, setProviderNormalCardApi] = useState<ProviderPayletterModel>();
  const [providerPopbillApi, setProviderPopbillApi] = useState<ProviderPopbillModel>();

  const { executeAsync: getProviderDetail } = useApiOperation(providerDetailAsync);
  const fetchProviderDetail = useCallback(
    async (providerId: string) => {
      const providerDetail = await getProviderDetail({ providerId: providerId });

      if (providerDetail && providerDetail.data && providerDetail.data.data && providerDetail.data.data.payletterList) {
        setProviderNormalCardApi(providerDetail.data.data.payletterList.find((item: ProviderPayletterModel) => item.clientType === "NORMAL"));
        setProviderPopbillApi(providerDetail.data.data.provider);
      }
    },
    [getProviderDetail],
  );
  /**
   * 계약 데이터 저장
   * @param editContract 수정된 계약 정보
   */
  const onSaveContractData = useCallback(
    async (editContract: any) => {
      // 계약명 필수값 검증
      if (!editContract.contractName || String(editContract.contractName).trim() === "") {
        setContractNameError("필수입력 항목입니다.");
        setEditConfrim(false);
        setSaveFlags(false);
        setPopupState(false);
        setEditable(true);
        return;
      }

      if (editContract.pgcodeOption?.items && editContract.pgcodeOption.items?.every((item: { isEnabled: boolean }) => !item.isEnabled)) {
        openToast({ content: "결제 가능 수단을 1개 이상 선택해 주세요.", isSystemToast: true });
        setEditConfrim(false);
        setSaveFlags(false);
        setPopupState(false);
        setEditable(true);
        return;
      }

      const send = _.cloneDeep(editContract!);
      let billContactList = editNonTaxNotiList.concat(editTaxNotiList);

      if (billContactList.length > 0) {
        let hasValidationError = false;
        billContactList.forEach((noti: ContractorNoti) => {
          if (noti.receiveNotiType === "TALK_OFFICIALDOC") {
            if ((noti.email === "", noti.contact === "")) {
              hasValidationError = true;
              openToast({ content: "공문 수신 대상 이메일과 휴대폰 정보를 입력해 주세요.", isSystemToast: true });
              return;
            }
          }
          if (noti.receiveNotiType !== "TALK_OFFICIALDOC") {
            if (noti.email === "") {
              hasValidationError = true;
              openToast({ content: "세금계산서 수신 대상자 이메일 정보를 입력해 주세요.", isSystemToast: true });
              return;
            }
          }
        });

        if (hasValidationError) {
          setEditConfrim(false);
          setSaveFlags(false);
          setPopupState(false);
          setEditable(true);
          return;
        } else {
          setPopupState(true);
          billContactList.forEach((noti: ContractorNoti) => {
            if (noti.receiveNotiType === "TALK_OFFICIALDOC") {
              noti.contact = parsedPhoneNumber(noti.contact!);
            }
          });
          send.billContactList = billContactList;

          const result: any = await editContractData(contract!, send);
          if (result.response.status > 199 && result.response.status < 300) {
            setCommonModals({
              message: "저장 되었습니다.",
              title: "계약 정보 변경",
            });
            setPopupState(true);
            setEditConfrim(false);
            setSaveFlags(false);
            setEditable(false);
            setData();
            return;
          }

          if (result.validationPass === false) {
            setCommonModals({
              message: result.message,
              title: "계약 정보 변경",
            });
            setPopupState(true);
            setSaveFlags(false);
            setEditConfrim(false);
            return;
          }
          setPopupState(false);
          setEditConfrim(false);
          setSaveFlags(false);
          setEditable(false);
        }
      }
    },
    [
      editNonTaxNotiList,
      editTaxNotiList,
      editContractData,
      contract,
      openToast,
      setCommonModals,
      setPopupState,
      setEditConfrim,
      setSaveFlags,
      setEditable,
      setData,
    ],
  );

  /**
   * 파트너 전자서명 확인
   * @returns 전자서명 가능 여부
   */
  // const getPartnerEsignCheck = async () => {
  //   const { data, status } = await getPartnerEsignData();
  //   if (status >= 200 && status < 300) {
  //     const { isESignonAvailable, isModuSignAvailable } = data.data;
  //     return { isESignonAvailable, isModuSignAvailable };
  //   }
  // };

  /**
   * 초기 데이터 설정
   * 계약 정보, 기간 옵션, 알림 목록 등의 초기 데이터를 설정합니다.
   */
  const initDatas = useCallback(async () => {
    if (detailData) {
      setEditContract(contract!);

      const options: Select[] = [];
      if (product.productType === "FULL_COURT") {
        const min = Number(product.minLeasePeriod);
        const max = Number(product.maxLeasePeriod);
        for (let i = min; i <= max; i++) {
          const option = { label: i + "개월", value: String(i) };
          options.push(option);
        }
      }
      if (product.productType === "OPEN_COURT") {
        const min = Number(product.minLeasePeriod);
        const max = Number(product.maxLeasePeriod);
        for (let i = min; i <= max; i++) {
          const option = { label: i + "개월", value: String(i) };
          options.push(option);
        }
      }
      setSelectedPeriodOptions(options);
      const sd = scheduleList?.find((schedule: BillingDetail) => schedule.supplyType === "RENTAL");

      const billContractorNoti = _.cloneDeep(contract?.billContactList);

      const officialDocNotifications = billContractorNoti!.filter((noti: ContractorNoti) => noti.receiveNotiType === "TALK_OFFICIALDOC");
      const taxInvoiceNotifications = billContractorNoti!.filter((noti: ContractorNoti) => noti.receiveNotiType !== "TALK_OFFICIALDOC");

      if (detailData.product.productType === "TIME_COURT" && taxInvoiceNotifications.length > 0) {
        setS2TaxNotiOne(taxInvoiceNotifications[0]);
      }

      officialDocNotifications.forEach((noti: ContractorNoti, index: number) => {
        noti.orderNum = index;
      });
      taxInvoiceNotifications.forEach((noti: ContractorNoti, index: number) => {
        noti.orderNum = index;
      });

      setEditNonTaxNotiList(officialDocNotifications);
      setEditTaxNotiList(taxInvoiceNotifications);
      setEditRentalSchedule(sd);
    }
  }, [detailData]);

  // 초기 데이터 로드
  useEffect(() => {
    initDatas();
    if (product?.providerId) {
      fetchProviderDetail(String(product.providerId));
    }
  }, [initDatas, product?.providerId, fetchProviderDetail]);

  // 저장 플래그 변경 시 처리
  useEffect(() => {
    if (saveFlags === true) {
      onSaveContractData(editContract);
    } else {
      //취소 클릭 시
      initDatas();
    }
  }, [saveFlags]);

  return (
    <>
      <div className="flex-center">
        <section className="contents-container__grid">
          <div className="contents-container__grid-index flex-start">
            <div className="flex-center">
              <p className="required">계약명(필수)</p>
              <BaseTooltip
                touchIcon={"QUESTION"}
                className="ml4 mt1"
                size={16}
                tooltip="계약명을 설정하면, 앱/알림톡/이메일 공문에 관리자가 입력한 정보가 출력됩니다."
              />
            </div>
          </div>
          <div className="contents-container__grid-contents">
            <div className="minmax300">
              <BaseInput
                readonly={
                  //신청취소/이용완료/해지완료' 상태일 경우 BizException 처리
                  contract?.contractStep === "APPLY_CANCEL" ||
                  contract?.contractStep === "USE_COMPLETE" ||
                  contract?.contractStep === "TERMINATE_COMPLETE"
                }
                name={"contractName"}
                value={editContract?.contractName ? String(editContract?.contractName) : ""}
                onChange={(value: string) => {
                  const updatedContract = _.cloneDeep(editContract!);
                  updatedContract.contractName = value;
                  setEditContract(updatedContract);
                  if (value.trim()) {
                    setContractNameError("");
                  }
                }}
                errorText={contractNameError}
              />
            </div>
          </div>
        </section>

        <section className="contents-container__grid">
          <div className="contents-container__grid-index flex-start">
            <div className="flex-center">
              <p>계약명(선택)</p>
              <BaseTooltip
                touchIcon={"QUESTION"}
                className="ml4 mt1"
                size={16}
                tooltip="방문자 셀프 등록 서비스에서 계약명(필수)와 계약명(선택) 모두 검색할 수 있습니다."
              />
            </div>
          </div>
          <div className="contents-container__grid-contents">
            <div className="minmax300">
              <BaseInput
                readonly={
                  //신청취소/이용완료/해지완료' 상태일 경우 BizException 처리 (계약명(필수)과 동일 정책)
                  contract?.contractStep === "APPLY_CANCEL" ||
                  contract?.contractStep === "USE_COMPLETE" ||
                  contract?.contractStep === "TERMINATE_COMPLETE"
                }
                name={"contractNickname"}
                placeholder="계약명을 입력해주세요"
                value={editContract?.contractNickname ? String(editContract?.contractNickname) : ""}
                onChange={(value: string) => {
                  const updatedContract = _.cloneDeep(editContract!);
                  updatedContract.contractNickname = value;
                  setEditContract(updatedContract);
                }}
              />
            </div>
          </div>
        </section>
      </div>
      <div className="flex-center">
        <section className={textGrey}>
          <div className="contents-container__grid-index">
            <p>계약자명</p>
          </div>
          <div className="contents-container__grid-contents">
            <div className="minmax300">
              <BaseInput
                readonly={
                  contract?.contractStep === "APPLY_RECEIVED" ||
                  contract?.contractStep === "APPLY_CANCEL" ||
                  contract?.contractStep === "USE_COMPLETE" ||
                  contract?.contractStep === "TERMINATE_COMPLETE"
                }
                name={"contractorName"}
                value={editContract?.contractorName ? String(editContract?.contractorName) : ""}
                onChange={(value: string) => {
                  const updatedContract = _.cloneDeep(editContract!);
                  updatedContract.contractorName = value;
                  setEditContract(updatedContract);
                }}
              />
            </div>
          </div>
        </section>
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p>계약일시</p>
          </div>
          <div className="contents-container__grid-contents">
            {detailData?.product?.productType !== "TIME_COURT" ? (
              <div className="flex-row flex-center-start">
                <div className="minmax300">
                  <BaseDatePicker
                    readonly={isUsePeriodLocked}
                    name={"contractDate"}
                    type="full"
                    selectedDate={editContract?.contractDate ? moment(editContract?.contractDate).toDate() : null}
                    setDate={(value: string) => {
                      const updatedContract = _.cloneDeep(editContract!);

                      updatedContract.contractDate = moment(value).format(YmdFormat.WITH_TIME_ZONE);
                      setEditContract(updatedContract);
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex-row flex-center-start">
                <div className="minmax180 mr8">
                  <p>{moment(editContract?.contractDate).format(ViewYmdFormat.FULL)}</p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <div className="flex-center">
        {detailData?.product?.productType !== "TIME_COURT" ? (
          <>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>이용 시작일시</p>
              </div>
              <div className="contents-container__grid-contents ">
                <div className="minmax300">
                  <BaseDatePicker
                    // 재계약의 계약시작일은 원계약 종료일 다음날로 고정이므로 계약 상태와 무관하게 수정할 수 없다
                    readonly={isUsePeriodLocked || isRenewal}
                    name={"contractStartTime"}
                    selectedDate={editContract?.contractStartTime ? moment(editContract?.contractStartTime).toDate() : null}
                    maxDate={editContract?.contractEndTime ? moment(editContract?.contractEndTime).toDate() : undefined}
                    setDate={(value: Date) => {
                      const updatedContract = _.cloneDeep(editContract!);

                      updatedContract.contractStartTime = moment(value).format(ViewYmdFormat.YYYY_MM_DD);
                      updatedContract.contractTerm = getContractTerm(
                        updatedContract.contractStartTime,
                        updatedContract.contractEndTime,
                        updatedContract.contractTerm,
                      );
                      setEditContract(updatedContract);
                    }}
                  />
                </div>
              </div>
            </section>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>이용 종료일시</p>
              </div>
              <div className="contents-container__grid-contents ">
                <div className="minmax300">
                  <BaseDatePicker
                    readonly={isUsePeriodLocked}
                    name={"contractEndTime"}
                    selectedDate={editContract?.contractEndTime ? moment(editContract?.contractEndTime).toDate() : null}
                    minDate={editContract?.contractStartTime ? moment(editContract?.contractStartTime).toDate() : undefined}
                    setDate={(value: Date) => {
                      const updatedContract = _.cloneDeep(editContract!);

                      updatedContract.contractEndTime = moment(value).format(ViewYmdFormat.YYYY_MM_DD);
                      updatedContract.contractTerm = getContractTerm(
                        updatedContract.contractStartTime,
                        updatedContract.contractEndTime,
                        updatedContract.contractTerm,
                      );
                      setEditContract(updatedContract);
                    }}
                  />
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>계약 ID</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="flex-row flex-center-start">
                  <span className="mr10">{contract?.contractId ? contract?.contractId : "-"}</span>
                  {textGrey !== "" && (
                    <BaseButton
                      title="계약 상품정보 스냅샷"
                      className="color-white"
                      disabled={
                        contract?.contractStep === "APPLY_RECEIVED" ||
                        contract?.contractStep === "APPLY_CANCEL" ||
                        contract?.contractStep === "APPLY_CONFIRM"
                      }
                      onClick={() => setIsSnapShotOpen(true)}
                    />
                  )}
                  <SnapShotModal
                    isOpen={isSnapShotOpen}
                    contractId={Number(contract?.contractId)}
                    detailData={detailData}
                    onClose={() => {
                      setIsSnapShotOpen(false);
                    }}
                  />
                </div>
              </div>
            </section>
            <section className="contents-container__grid" />
          </>
        )}
      </div>
      {detailData?.product?.productType !== "TIME_COURT" ? (
        <div className="flex-center">
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>보증금</p>
            </div>
            <div className="contents-container__grid-contents">
              <p>{numberToStringWithComma(Number(editContract?.earnestAmount) + Number(editContract?.balanceAmount)) + " 원"}</p>
            </div>
          </section>

          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <div className="flex-center font14">
                이용료(부가세 별도)
                <BaseTooltip
                  contents={""}
                  tooltip={"계약 시 협의된 부가세를 제외한 월 이용료를 입력해 주세요."}
                  type={"normal"}
                  className={"tooltip__trigger-icon"}
                />
              </div>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-row flex-center-start ">
                <div className="minmax140 mr8">
                  <BaseInput
                    readonly={
                      disabledBeforeContractPlan.includes(contract?.contractStep!) === false || detailData?.product?.productType === "TIME_COURT"
                    }
                    name={"payAmount"}
                    type="text"
                    className={"text-right"}
                    value={numberToStringWithComma(Number(_.replace(String(editContract?.payAmount), new RegExp(",", "g"), "")))}
                    onChange={(value: string) => {
                      let payAmount = _.replace(String(value), new RegExp(",", "g"), "");

                      payAmount = _.replace(String(payAmount), new RegExp("-", "g"), "");

                      const updatedContract = _.cloneDeep(editContract!);
                      updatedContract.payAmount = Number(payAmount);

                      setEditContract(updatedContract);
                    }}
                  />
                </div>
                <BaseButton
                  disabled={!priceChangeStatus2.includes(contract?.contractStep!)}
                  title={"청구 금액 확인"}
                  className="color-white"
                  onClick={() => setIsModifyAmountOpen(true)}
                />

                <ModifyAmountModalAccordion
                  detailData={detailData}
                  isOpen={isModifyAmountOpen}
                  onClose={() => {
                    setIsModifyAmountOpen(false);
                  }}
                  onClick={() => {
                    setIsModifyAmountOpen(false);
                  }}
                  scheduleId={Number(editRentalSchedule?.scheduleId)}
                  viewOnly={true}
                  setCommonModals={setCommonModals}
                  setPopupState={setPopupState}
                  setEditConfrim={setEditConfrim}
                />
              </div>
            </div>
          </section>
        </div>
      ) : (
        <div className="flex-center">
          <BaseSection
            title={"세금계산서 수신 이메일"}
            children={
              <div className="minmax300">
                <div className="flex-row flex-center-start">
                  <BaseInput
                    readonly={
                      contract?.contractStep === "APPLY_CANCEL" ||
                      contract?.contractStep === "TERMINATE_COMPLETE" ||
                      contract?.contractStep === "APPLY_CONFIRM" ||
                      contract?.contractStep === "USE_COMPLETE"
                    }
                    name={`s2TaxNotiOne.email`}
                    value={s2TaxNotiOne.email}
                    onChange={(value: string) => {
                      const d_s2TaxNotiOne = _.cloneDeep(s2TaxNotiOne!);
                      const updatedTaxNotiList = _.cloneDeep(editTaxNotiList);

                      if (checkEmail.test(value) === false) {
                        d_s2TaxNotiOne.errorTextEmail = "이메일 형식이 아닙니다.";
                        d_s2TaxNotiOne.email = "";
                        setS2TaxNotiOne(d_s2TaxNotiOne);
                      } else {
                        d_s2TaxNotiOne.errorTextEmail = "";
                        d_s2TaxNotiOne.email = value;
                        updatedTaxNotiList.forEach((notiItem: any, idx: number) => {
                          if (String(s2TaxNotiOne.billContactId) === String(notiItem.billContactId)) {
                            notiItem.email = value;
                            notiItem.cmdType = "U";
                          }
                        });

                        setS2TaxNotiOne(d_s2TaxNotiOne);
                        setEditTaxNotiList(updatedTaxNotiList);
                      }
                    }}
                    errorText={s2TaxNotiOne.errorTextEmail ? s2TaxNotiOne.errorTextEmail : ""}
                  />
                </div>
              </div>
            }
          />
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>계약 형태</p>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-row flex-center-start">
                <BaseRadio
                  id="contractType1"
                  name="contractType"
                  // value={"true"}
                  label="직접 계약"
                  // checked={String(isEsign) === "false" ? true : false}
                  checked={contractSignType.directContract}
                  onChange={() => {
                    // setIsEsign(false);
                    onChangeContractSignType({ directContract: true, isEsign: false, isModuSign: false });
                  }}
                  disabled={disabledEsignEdit.includes(contract?.contractStep!)}
                ></BaseRadio>
                <BaseRadio
                  id="contractType2"
                  name="contractType"
                  // value={"false"}
                  className="mx10"
                  label="이싸인온"
                  // checked={String(isEsign) === "true" ? true : false}
                  checked={contractSignType.isEsign}
                  onChange={async () => {
                    const hasEsignPermission = await getPartnerEsignCheck();
                    if (hasEsignPermission?.isESignonAvailable) {
                      // setIsEsign(true);
                      onChangeContractSignType({ directContract: false, isEsign: true, isModuSign: false });
                    } else {
                      setCommonModals({
                        message: "전자 서명을 이용하시려면 관리자에게 문의해 주세요.",
                        title: "파트너 권한이 없습니다.",
                      });
                      setPopupState(true);
                    }
                  }}
                  disabled={disabledEsignEdit.includes(contract?.contractStep!)}
                ></BaseRadio>
                <BaseRadio
                  id="contractType3"
                  name="contractType"
                  // value={"false"}
                  label="모두싸인"
                  // checked={String(isEsign) === "true" ? true : false}
                  checked={contractSignType.isModuSign}
                  onChange={async () => {
                    const hasModuSignPermission = await getPartnerEsignCheck();
                    if (hasModuSignPermission?.isModuSignAvailable) {
                      onChangeContractSignType({ directContract: false, isEsign: false, isModuSign: true });
                    } else {
                      setCommonModals({
                        message: "전자 서명을 이용하시려면 관리자에게 문의해 주세요.",
                        title: "파트너 권한이 없습니다.",
                      });
                      setPopupState(true);
                    }
                  }}
                  disabled={disabledEsignEdit.includes(contract?.contractStep!)}
                />
              </div>
            </div>
          </section>
        </div>
      )}
      <div className="flex-center">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>ㄴ 계약금</p>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-row flex-center-start">
                <div className="minmax120 mr8">
                  <BaseSelect
                    isDisabled={disabledBeforeContractPlan.includes(contract?.contractStep!) === false}
                    name={"earnestPayStatus"}
                    value={editContract?.earnestPayStatus}
                    stateOptions={payStatusOption}
                    setStateValue={(text: string) => {
                      const updatedContract = _.cloneDeep(editContract!);

                      updatedContract.earnestPayStatus = text;
                      setEditContract(updatedContract);
                    }}
                  />
                </div>
                <div className="minmax120">
                  <BaseInput
                    readonly={disabledBeforeContractPlan.includes(contract?.contractStep!) === false}
                    name={"earnestAmount"}
                    type="text"
                    className={"text-right"}
                    value={numberToStringWithComma(Number(_.replace(String(editContract?.earnestAmount), new RegExp(",", "g"), "")))}
                    onChange={(value: string) => {
                      let earnestAmount = _.replace(String(value), new RegExp(",", "g"), "");
                      earnestAmount = _.replace(String(earnestAmount), new RegExp("-", "g"), "");
                      const updatedContract = _.cloneDeep(editContract!);

                      updatedContract.earnestAmount = Number(earnestAmount);
                      updatedContract.depositAmount = Number(updatedContract.earnestAmount) + Number(updatedContract.balanceAmount);
                      setEditContract(updatedContract);
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
        {detailData?.product?.productType !== "TIME_COURT" && (
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>납부 유형</p>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-row flex-center-start">
                <BaseRadio
                  id="costType1"
                  name="costType"
                  className="mr10"
                  value={"COST_BILLING_MONTHLY"}
                  label="정기 납부"
                  checked={editContract?.costType === "COST_BILLING_MONTHLY" ? true : false}
                  onChange={() => {
                    const updatedContract = _.cloneDeep(editContract!);
                    updatedContract.costType = "COST_BILLING_MONTHLY";
                    setEditContract(updatedContract);
                  }}
                  disabled={disabledBeforeContractPlan.includes(contract?.contractStep!) === false}
                ></BaseRadio>
                <BaseRadio
                  id="costType2"
                  name="costType"
                  value={"COST_BILLING_ONE_TIME"}
                  label="일시 납부"
                  checked={editContract?.costType === "COST_BILLING_ONE_TIME" ? true : false}
                  onChange={() => {
                    const updatedContract = _.cloneDeep(editContract!);
                    updatedContract.costType = "COST_BILLING_ONE_TIME";
                    setEditContract(updatedContract);
                  }}
                  disabled={disabledBeforeContractPlan.includes(contract?.contractStep!) === false}
                ></BaseRadio>
              </div>
            </div>
          </section>
        )}
      </div>
      <div className="flex-center">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>ㄴ 잔금</p>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-row flex-center-start">
                <div className="minmax120 mr8">
                  <BaseSelect
                    isDisabled={disabledBeforeContractAccept.includes(contract?.contractStep!) === false}
                    name={"balancePayStatus"}
                    value={editContract?.balancePayStatus}
                    stateOptions={payStatusOption}
                    setStateValue={(text: string) => {
                      const updatedContract = _.cloneDeep(editContract!);

                      updatedContract.balancePayStatus = text;
                      setEditContract(updatedContract);
                    }}
                  />
                </div>
                <div className="minmax120">
                  <BaseInput
                    readonly={disabledBeforeContractPlan.includes(contract?.contractStep!) === false}
                    name={"balanceAmount"}
                    type="text"
                    className={"text-right"}
                    value={numberToStringWithComma(Number(_.replace(String(editContract?.balanceAmount), new RegExp(",", "g"), "")))}
                    onChange={(value: string) => {
                      let balanceAmount = _.replace(String(value), new RegExp(",", "g"), "");
                      balanceAmount = _.replace(String(balanceAmount), new RegExp("-", "g"), "");
                      const updatedContract = _.cloneDeep(editContract!);
                      updatedContract.balanceAmount = Number(balanceAmount);
                      updatedContract.depositAmount = Number(updatedContract.earnestAmount) + Number(updatedContract.balanceAmount);
                      setEditContract(updatedContract);
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
      <div className="flex-center">
        {/* 계약 ID */}
        {detailData?.product?.productType !== "TIME_COURT" && (
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>계약 ID</p>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-row flex-center-start">
                <span className="mr10">{contract?.contractId ? contract?.contractId : "-"}</span>
                {textGrey !== "" && (
                  <BaseButton
                    title="계약 상품정보 스냅샷"
                    className="color-white"
                    disabled={
                      contract?.contractStep === "APPLY_RECEIVED" ||
                      contract?.contractStep === "APPLY_CANCEL" ||
                      contract?.contractStep === "APPLY_CONFIRM"
                    }
                    onClick={() => setIsSnapShotOpen(true)}
                  />
                )}
                <SnapShotModal
                  isOpen={isSnapShotOpen}
                  contractId={Number(contract?.contractId)}
                  detailData={detailData}
                  onClose={() => {
                    setIsSnapShotOpen(false);
                  }}
                />
              </div>
            </div>
          </section>
        )}
        {detailData?.product?.productType !== "TIME_COURT" && (
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>계약 형태</p>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-row flex-center-start">
                <BaseRadio
                  id="contractType1"
                  name="contractType"
                  // value={"true"}
                  label="직접 계약"
                  // checked={String(isEsign) === "false" ? true : false}
                  checked={contractSignType.directContract}
                  onChange={() => {
                    // setIsEsign(false);
                    onChangeContractSignType({ directContract: true, isEsign: false, isModuSign: false });
                  }}
                  disabled={disabledEsignEdit.includes(contract?.contractStep!)}
                ></BaseRadio>
                <BaseRadio
                  id="contractType2"
                  name="contractType"
                  className="mx10"
                  // value={"false"}
                  label="이싸인온"
                  // checked={String(isEsign) === "true" ? true : false}
                  checked={contractSignType.isEsign}
                  onChange={async () => {
                    const hasEsignPermission = await getPartnerEsignCheck();
                    if (hasEsignPermission?.isESignonAvailable) {
                      // setIsEsign(true);
                      onChangeContractSignType({ directContract: false, isEsign: true, isModuSign: false });
                    } else {
                      setCommonModals({
                        message: "전자 서명을 이용하시려면 관리자에게 문의해 주세요.",
                        title: "파트너 권한이 없습니다.",
                      });
                      setPopupState(true);
                    }
                  }}
                  disabled={disabledEsignEdit.includes(contract?.contractStep!)}
                />
                <BaseRadio
                  id="contractType3"
                  name="contractType"
                  label="모두싸인"
                  checked={contractSignType.isModuSign}
                  onChange={async () => {
                    const hasModuSignPermission = await getPartnerEsignCheck();

                    if (hasModuSignPermission?.isModuSignAvailable) {
                      onChangeContractSignType({ directContract: false, isEsign: false, isModuSign: true });
                    } else {
                      setCommonModals({
                        message: "전자 서명을 이용하시려면 관리자에게 문의해 주세요.",
                        title: "파트너 권한이 없습니다.",
                      });
                      setPopupState(true);
                    }
                  }}
                  disabled={disabledEsignEdit.includes(contract?.contractStep!)}
                />
              </div>
            </div>
          </section>
        )}
      </div>
      {/* 직접 계약 */}
      {contractSignType.directContract && <DirectFileUpload contract={contract!} editContract={editContract!} setEditContract={setEditContract} />}

      {/* {isEsign ? ( */}
      {contractSignType.isEsign && (
        <EsignOnLink
          detailData={detailData}
          viewOnly={false}
          setData={setData!}
          setEditable={setEditable}
          initMainData={initDatas}
          // setIsEsign={setIsEsign}
          setIsEsign={(checked: boolean) => {
            onChangeContractSignType({ directContract: false, isEsign: true, isModuSign: false });
          }}
          data={editContract}
        />
      )}
      {/* 모두싸인 */}
      {contractSignType.isModuSign && <ModuSignContractSection setEditable={setEditable} onChangeContractSignType={onChangeContractSignType} />}
      {/* 결제 가능 수단 */}
      {detailData?.product?.productType === "TIME_COURT" && (
        <div className="flex-start">
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p></p>
            </div>
            <div className="contents-container__grid-contents"></div>
          </section>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>결제 가능 수단</p>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-center flex-row flex-center-start">
                {contract?.contractStep! === ContractStep.CONTRACT_PLAN ? (
                  editContract?.pgcodeOption?.items?.map((item) => (
                    <BaseCheckbox
                      key={item.pgcode}
                      id={item.pgcode}
                      name={item.pgcode}
                      checked={item.isEnabled}
                      className="mr16"
                      label={changePgcodeLabel(item.pgcode)}
                      onChange={(checked: boolean) => {
                        if (!checked) {
                          const newItems = editContract.pgcodeOption!.items.map((i) => (i.pgcode === item.pgcode ? { ...i, isEnabled: checked } : i));
                          setEditContract({ ...editContract, pgcodeOption: { items: newItems } });
                          return;
                        }

                        // creditcard일 경우 -> 페이레터 검증
                        if (item.pgcode === "creditcard") {
                          if (providerNormalCardApi?.isVerifiedPaymentKey !== true || providerNormalCardApi?.isVerifiedSearchKey !== true) {
                            openToast({ content: "선택할 수 없습니다" });
                            return;
                          }
                        }

                        // virtualaccount일 경우 -> 페이레터, 팝빌 검증
                        if (item.pgcode === "virtualaccount") {
                          if (
                            providerNormalCardApi?.isVerifiedPaymentKey !== true ||
                            providerNormalCardApi?.isVerifiedSearchKey !== true ||
                            providerPopbillApi?.isVerifiedTaxInvoice !== true ||
                            providerPopbillApi?.isVerifiedCashbill !== true
                          ) {
                            openToast({ content: "선택할 수 없습니다" });
                            return;
                          }
                        }

                        const newItems = editContract.pgcodeOption!.items.map((i) => (i.pgcode === item.pgcode ? { ...i, isEnabled: checked } : i));
                        setEditContract({ ...editContract, pgcodeOption: { items: newItems } });
                      }}
                    />
                  ))
                ) : (
                  <p>
                    <span className="font14">{getPgcodeLabel(contract?.pgcodeOption)}</span>
                  </p>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
      <div className="flex-start">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>관리비 청구 유형</p>
            </div>
            <div className="contents-container__grid-contents">
              <div className="flex-row flex-center-start">
                <>
                  <BaseRadio
                    disabled={contract?.contractStep! === ContractStep.APPLY_CANCEL}
                    id={"isMntContain_true"}
                    name={"isMntContain"}
                    label="관리비 포함"
                    value="true"
                    className="mr16"
                    checked={String(editContract?.isMntContain) === "true" ? true : false}
                    onChange={() => {
                      const updatedContract = _.cloneDeep(editContract!);
                      updatedContract.isMntContain = true;
                      setEditContract(updatedContract);
                    }}
                  />
                  <BaseRadio
                    disabled={contract?.contractStep! === ContractStep.APPLY_CANCEL}
                    id={"isMntContain_false"}
                    name={"isMntContain"}
                    label="관리비 별도"
                    value="false"
                    checked={String(editContract?.isMntContain) === "false" ? true : false}
                    onChange={(e) => {
                      const updatedContract = _.cloneDeep(editContract!);
                      if (detailData.product.productType === "NON_RESIDENT_OFFICE" || detailData.product.productType === "FULL_COURT") {
                        if (detailData.provider?.provider?.isVerifiedTaxInvoice === false) {
                          setBaseModal({
                            isOpen: true,
                            title: `세금계산서 자동 발행 설정에 실패했습니다`,
                            children:
                              "해당 프로바이더의 세금계산서 발행 설정이 비활성화되어 홈택스에서 세금계산서를 발행해 주세요. 자동 발행을 원하시면 관리자에게 문의하세요.",
                            btnRightTitle: "확인",
                          });
                          return false;
                        } else {
                          updatedContract.isMntContain = false;
                          setEditContract(updatedContract);
                        }
                      } else {
                        updatedContract.isMntContain = false;
                        setEditContract(updatedContract);
                      }
                    }}
                  />
                </>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* v.1.32.4  ps-1440 건으로 주석처리 (Harry)*/}
      {/* <div className="flex-start">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <div className="">
            <BaseSection
              title={"공문 수신자"}
              children={
                <div className="contents-container__grid-contents">
                  {editNonTaxNotiList?.map(
                    (noti: ContractorNoti, index: number) =>
                      noti.isDeleted === false && (
                        <div className="flex-row flex-center" key={index}>
                          <>
                            <section className="contents-container__grid minmax435">
                              <div className="contents-container__grid-index">
                                <p>이메일</p>
                              </div>
                              <div className="contents-container__grid-contents">
                                <div className="minmax240 flex-row flex-center-start">
                                  <BaseInput
                                    readonly={
                                      contract?.contractStep === "APPLY_CANCEL" ||
                                      contract?.contractStep === "TERMINATE_COMPLETE" ||
                                      contract?.contractStep === "APPLY_CONFIRM" ||
                                      contract?.contractStep === "USE_COMPLETE"
                                    }
                                    className="minmax210"
                                    name={`billContact.email${index}`}
                                    value={noti.email}
                                    onChange={(value: string) => {
                                      const clonedNonTaxNotiList = _.cloneDeep(editNonTaxNotiList!);

                                      const duplicateItems = clonedNonTaxNotiList.filter(
                                        (notiItem: any, idx: number) => idx !== index && notiItem.email === value && !notiItem.isDeleted,
                                      );

                                      if (duplicateItems.length === 0) {
                                        const updatedNotiList = clonedNonTaxNotiList.map((notiItem: any, idx: number) => {
                                          if (_.isEqual(notiItem, noti)) {
                                            notiItem.errorTextEmail = "";
                                            notiItem.email = value;
                                            if (Number(noti.billContactId) !== 0) {
                                              notiItem.cmdType = "U";
                                            }
                                          }
                                          return notiItem;
                                        });
                                        setEditNonTaxNotiList(updatedNotiList);
                                      } else {
                                        const updatedNotiList = clonedNonTaxNotiList.map((notiItem: any, idx: number) => {
                                          if (index !== idx && notiItem.email === value) {
                                            notiItem.errorTextEmail = "이미 등록된 연락처 입니다.";
                                          }
                                          return notiItem;
                                        });
                                        setEditNonTaxNotiList(updatedNotiList);
                                      }
                                    }}
                                    onBlur={() => {
                                      const clonedNonTaxNotiList = _.cloneDeep(editNonTaxNotiList!);

                                      if (checkEmail.test(noti.email!) === false) {
                                        const updatedNotiList = clonedNonTaxNotiList.map((notiItem: any, idx: number) => {
                                          if (idx === index) {
                                            notiItem.errorTextEmail = "이메일 형식이 아닙니다.";
                                            notiItem.email = "";
                                          }
                                          return notiItem;
                                        });
                                        setEditNonTaxNotiList(updatedNotiList);
                                      }
                                    }}
                                    errorText={noti.errorTextEmail ? noti.errorTextEmail : ""}
                                  />
                                </div>
                              </div>
                            </section>
                            <section className="contents-container__grid">
                              <div className="contents-container__grid-index">
                                <p>휴대폰</p>
                              </div>
                              <div className="contents-container__grid-contents">
                                <div className="minmax240 flex-row flex-center-start">
                                  <BaseInput
                                    type="phone"
                                    readonly={
                                      contract?.contractStep === "APPLY_CANCEL" ||
                                      contract?.contractStep === "TERMINATE_COMPLETE" ||
                                      contract?.contractStep === "APPLY_CONFIRM" ||
                                      contract?.contractStep === "USE_COMPLETE"
                                    }
                                    name={`billContact.contact${index}`}
                                    value={noti.contact !== undefined ? String(formatPhoneNumber(noti.contact!)) : ""}
                                    onChange={(value: string) => {
                                      const clonedNonTaxNotiList = _.cloneDeep(editNonTaxNotiList);

                                      const duplicateItems = clonedNonTaxNotiList.filter(
                                        (notiItem: any, idx: number) => idx !== index && notiItem.contact === value && !notiItem.isDeleted,
                                      );

                                      if (duplicateItems.length === 0) {
                                        const updatedNotiList = clonedNonTaxNotiList?.map((notiItem: any, idx: number) => {
                                          if (_.isEqual(notiItem, noti)) {
                                            notiItem.errorTextContact = "";
                                            notiItem.contact = value;
                                            if (Number(noti.billContactId) !== 0) {
                                              notiItem.cmdType = "U";
                                            }
                                          }
                                          return notiItem;
                                        });
                                        setEditNonTaxNotiList(updatedNotiList);
                                      } else {
                                        const updatedNotiList = clonedNonTaxNotiList?.map((notiItem: any, idx: number) => {
                                          if (index !== idx && notiItem.contact === value) {
                                            notiItem.errorTextContact = "이미 등록된 연락처 입니다.";
                                          }
                                          return notiItem;
                                        });
                                        setEditNonTaxNotiList(updatedNotiList);
                                      }
                                    }}
                                    onBlur={() => {
                                      const clonedNonTaxNotiList = _.cloneDeep(editNonTaxNotiList);

                                      if (validatePhoneNumberReturnBoolean(noti.contact!) === false) {
                                        const updatedNotiList = clonedNonTaxNotiList.map((notiItem: any, idx: number) => {
                                          if (idx === index) {
                                            notiItem.errorTextContact = "휴대폰 번호 형식이 아닙니다.";
                                            notiItem.contact = "";
                                          }
                                          return notiItem;
                                        });
                                        setEditNonTaxNotiList(updatedNotiList);
                                      }
                                    }}
                                    errorText={noti.errorTextContact ? noti.errorTextContact : ""}
                                  />
                                  {index === 0 && (
                                    <button
                                      disabled={editRentalSchedule?.isAutoSend === false}
                                      className="base-add-btn ml10"
                                      onClick={(e: any) => {
                                        e.preventDefault();
                                        const clonedNonTaxNotiList = _.cloneDeep(editNonTaxNotiList!);
                                        const activeNotifications = clonedNonTaxNotiList.filter((notiItem: any) => notiItem.isDeleted === false);
                                        const lastNotification = _.sortBy(activeNotifications, ["orderNum"]).reverse()[0];

                                        if (activeNotifications && activeNotifications.length < 5) {
                                          const newNotification = {
                                            billContactId: 0,
                                            contractId: contract?.contractId,
                                            receiveNotiType: "TALK_OFFICIALDOC",
                                            contact: "",
                                            email: "",
                                            description: "",
                                            isDeleted: false,
                                            errorText: "",
                                            cmdType: "C",
                                            orderNum: lastNotification.orderNum + 1,
                                          };

                                          clonedNonTaxNotiList.push(newNotification);
                                          setEditNonTaxNotiList(clonedNonTaxNotiList);
                                        }
                                        return;
                                      }}
                                    />
                                  )}
                                  {index > 0 && (
                                    <button
                                      title="삭제"
                                      disabled={editRentalSchedule?.isAutoSend === false}
                                      onClick={(e: any) => {
                                        e.preventDefault();
                                        let clonedNonTaxNotiList = _.cloneDeep(editNonTaxNotiList);
                                        const foundNotification = clonedNonTaxNotiList!.find(
                                          (notiItem: any, idx: number) => idx === index && Number(notiItem.billContactId) === 0,
                                        );

                                        if (foundNotification) {
                                          clonedNonTaxNotiList = clonedNonTaxNotiList.filter((notiItem: any, idx: number) => idx !== index);
                                        } else {
                                          clonedNonTaxNotiList.forEach((notiItem: any, idx: number) => {
                                            if (idx === index && Number(notiItem.billContactId) !== 0) {
                                              notiItem.isDeleted = true;
                                              notiItem.cmdType = "D";
                                            }
                                          });
                                        }

                                        setEditNonTaxNotiList(clonedNonTaxNotiList);

                                        return;
                                      }}
                                      className="base-erase-btn size-large ml10"
                                    />
                                  )}
                                </div>
                              </div>
                            </section>
                          </>
                        </div>
                      ),
                  )}
                </div>
              }
            />
          </div>
        )}
      </div>
      {detailData?.product?.productType !== "TIME_COURT" && editRentalSchedule?.isTaxInvoiceIssue && (
        <div className="flex-start">
          <BaseSection
            title={"세금계산서 수신자"}
            children={
              <div className="contents-container__grid-contents">
                <div className="">
                  {editTaxNotiList.map(
                    (noti: any, index: number) =>
                      noti.isDeleted === false && (
                        <div className="flex-row flex-center" key={index}>
                          <>
                            <section className="contents-container__grid">
                              <div className="contents-container__grid-index">
                                <p>이메일</p>
                              </div>
                              <div className="contents-container__grid-contents">
                                <div className="minmax240 flex-row flex-center-start">
                                  <BaseInput
                                    readonly={
                                      contract?.contractStep === "APPLY_CANCEL" ||
                                      contract?.contractStep === "TERMINATE_COMPLETE" ||
                                      contract?.contractStep === "APPLY_CONFIRM" ||
                                      contract?.contractStep === "USE_COMPLETE"
                                    }
                                    name={`billContact.email${index}`}
                                    value={noti.email}
                                    onChange={(value: string) => {
                                      const clonedTaxNotiList = _.cloneDeep(editTaxNotiList!);

                                      const duplicateItems = clonedTaxNotiList.filter(
                                        (notiItem: any, idx: number) => idx !== notiItem.orderNum && notiItem.email === value && !notiItem.isDeleted,
                                      );

                                      if (duplicateItems.length === 0) {
                                        const updatedTaxNotiList = clonedTaxNotiList.map((notiItem: any, idx: number) => {
                                          if (_.isEqual(notiItem, noti)) {
                                            notiItem.errorTextEmail = "";
                                            notiItem.email = value;
                                            if (Number(noti.billContactId) !== 0) {
                                              notiItem.cmdType = "U";
                                            }
                                          }
                                          return notiItem;
                                        });
                                        setEditTaxNotiList(updatedTaxNotiList);
                                      } else {
                                        const updatedTaxNotiList = clonedTaxNotiList.map((notiItem: any, idx: number) => {
                                          if (index !== idx && notiItem.email === value) {
                                            notiItem.errorTextEmail = "이미 등록된 연락처 입니다.";
                                          }
                                          return notiItem;
                                        });
                                        setEditTaxNotiList(updatedTaxNotiList);
                                      }
                                    }}
                                    onBlur={() => {
                                      const clonedTaxNotiList = _.cloneDeep(editTaxNotiList!);
                                      const updatedTaxNotiList = clonedTaxNotiList.map((notiItem: any, idx: number) => {
                                        if (checkEmail.test(noti.email!) === false) {
                                          if (notiItem.orderNum === index) {
                                            notiItem.errorTextEmail = "이메일 형식이 아닙니다.";
                                            notiItem.email = "";
                                          }
                                          return notiItem;
                                        } else {
                                          if (notiItem.orderNum === index) {
                                            notiItem.errorTextEmail = "";
                                          }
                                          return notiItem;
                                        }
                                      });
                                      setEditTaxNotiList(updatedTaxNotiList);
                                    }}
                                    errorText={noti.errorTextEmail ? noti.errorTextEmail : ""}
                                  />
                                  {index === 0 && (
                                    <button
                                      disabled={editRentalSchedule?.regularPgcode === "virtualaccount"}
                                      className="base-add-btn ml10"
                                      onClick={(e: any) => {
                                        e.preventDefault();
                                        const clonedTaxNotiList = _.cloneDeep(editTaxNotiList);
                                        const activeTaxNotifications = clonedTaxNotiList.filter((notiItem: any) => notiItem.isDeleted === false);

                                        const lastTaxNotification = _.sortBy(activeTaxNotifications, ["orderNum"]).reverse()[0];
                                        if (activeTaxNotifications.length < 3) {
                                          const newTaxNotification = {
                                            billContactId: 0,
                                            contractId: contract?.contractId,
                                            receiveNotiType: "TAX_INVOICE",
                                            contact: "",
                                            email: "",
                                            description: "",
                                            isDeleted: false,
                                            errorText: "",
                                            cmdType: "C",
                                            orderNum: lastTaxNotification.orderNum + 1,
                                          };

                                          clonedTaxNotiList.push(newTaxNotification);
                                          setEditTaxNotiList(clonedTaxNotiList);
                                        }
                                        return;
                                      }}
                                    />
                                  )}
                                  {index > 0 && (
                                    <button
                                      title="삭제"
                                      disabled={editRentalSchedule?.regularPgcode === "virtualaccount"}
                                      onClick={(e: any) => {
                                        e.preventDefault();
                                        let clonedTaxNotiList = _.cloneDeep(editTaxNotiList);
                                        const foundTaxNotification = clonedTaxNotiList.find(
                                          (notiItem: any, idx: number) => idx === index && Number(notiItem.billContactId) === 0,
                                        );
                                        if (foundTaxNotification) {
                                          clonedTaxNotiList = clonedTaxNotiList.filter((notiItem: any, idx: number) => idx !== index);
                                        } else {
                                          clonedTaxNotiList.forEach((notiItem: any, idx: number) => {
                                            if (idx === index && Number(notiItem.billContactId) !== 0) {
                                              notiItem.isDeleted = true;
                                              notiItem.cmdType = "D";
                                            }
                                          });
                                        }
                                        setEditTaxNotiList(clonedTaxNotiList);

                                        return;
                                      }}
                                      className="base-erase-btn size-large ml10"
                                    />
                                  )}
                                </div>
                              </div>
                            </section>
                          </>
                        </div>
                      ),
                  )}
                </div>
              </div>
            }
          />
        </div>
      )} */}
    </>
  );
};
export default ContractInfoEdit;
